<script setup lang="ts">
import { computed } from 'vue';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent'
import Tag from 'primevue/tag';
import { getRiskLevelDisplay } from '@/utils/kyc';
import { formatCnae } from '@/utils/cnae';
import type { KycCategory } from '@/types/kyc';
import cnaeDescriptions from '@/assets/cnae_descriptions.json';

type CnaeCode = keyof typeof cnaeDescriptions;

// Estrutura dos dados de atividade econômica
interface EconomicActivity {
  cnae7: string;
  cnae_text: string;
  is_main_branch: boolean;
  is_primary_cnae: boolean;
  level_value: number;
  rule_id: string | null;
}

interface Props {
  category: KycCategory;
}

const props = defineProps<Props>();

const activities = computed<EconomicActivity[]>(() => props.category.data_json || []);

const primaryActivity = computed(() => activities.value.find(a => a.is_primary_cnae));

const secondaryActivities = computed(() => {
  return activities.value
    .filter(a => !a.is_primary_cnae)
    .sort((a, b) => (b.level_value || 0) - (a.level_value || 0));
});

/** Tag props aligned with `RISK_LEVEL_CONFIG` (label + colors), not Prime severity presets. */
const getRiskLevelTagAttrs = (levelValue: number | null | undefined) => {
  const d = getRiskLevelDisplay(levelValue);
  return {
    value: d.label,
    style: {
      backgroundColor: d.hexBgColor,
      color: d.hexColor,
    },
    class: [d.borderClass, 'border'],
  };
};

const primaryRiskTag = computed(() => {
  if (!primaryActivity.value) return null;
  return getRiskLevelTagAttrs(primaryActivity.value.level_value);
});

const getCnaeHierarchy = (cnae7: string) => {
  if (!cnae7 || !cnaeDescriptions[cnae7 as CnaeCode]) {
    return [];
  }

  const subclasseCode = cnae7;
  const classeCode = subclasseCode.substring(0, 5);
  const grupoCode = subclasseCode.substring(0, 3);
  const divisaoCode = subclasseCode.substring(0, 2);

  return [
    { level: 'Divisão', code: divisaoCode, description: '(Descrição não disponível)' },
    { level: 'Grupo', code: `${grupoCode.slice(0, 2)}.${grupoCode.slice(2)}`, description: '(Descrição não disponível)' },
    { level: 'Classe', code: `${classeCode.slice(0, 4)}-${classeCode.slice(4)}`, description: cnaeDescriptions[subclasseCode as CnaeCode]?.class_description },
    { level: 'Subclasse', code: formatCnae(subclasseCode), description: cnaeDescriptions[subclasseCode as CnaeCode]?.description },
  ];
};

</script>

<template>
  <div class="mt-3">
    <!-- Atividade principal -->
    <h5 class="font-semibold mb-2 text-base">Atividade econômica principal</h5>
    <Accordion class="rounded-lg shadow-lg border border-blue-300">
      <AccordionPanel value="0" class="rounded-lg">
        <AccordionHeader class="rounded-lg">
          <div class="flex gap-4">
            <div>
              <div class="text-sm font-medium text-gray-500 mb-1">Nível de risco</div>
              <Tag v-if="primaryRiskTag" v-bind="primaryRiskTag" />
            </div>
            <div class="md:col-span-2">
              <div class="text-sm font-medium text-gray-500 mb-1">Atividade econômica (CNAE)</div>
              <div class="font-medium text-gray-900">{{ primaryActivity ? primaryActivity.cnae_text : '' }}</div>
              <div class="text-xs text-gray-600 font-mono">{{ primaryActivity ? formatCnae(primaryActivity.cnae7) : '' }}
              </div>
            </div>
          </div>
        </AccordionHeader>
        <AccordionContent class="rounded-lg">
          <div class="p-3 bg-gray-50">
            <h6 class="font-semibold text-sm mb-2">Hierarquia do CNAE</h6>
            <ul v-if="primaryActivity" class="space-y-2">
              <li v-for="(item, index) in getCnaeHierarchy(primaryActivity.cnae7)" :key="item.level"
                class="flex items-baseline">
                <span class="font-semibold text-gray-600 w-24">{{ item.level }}:</span>
                <div class="flex-1 indent-4" :style="{ paddingLeft: `${index * 1.5}rem` }">
                  <span class="font-mono text-gray-800">{{ item.code }}</span> -
                  <span class="text-gray-700">{{ item.description }}</span>
                </div>
              </li>
            </ul>
          </div>
        </AccordionContent>
      </AccordionPanel>
    </Accordion>



    <!-- Atividades secundárias -->
    <div
      v-if="secondaryActivities.length > 0"
      class="mt-6 rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-md ring-1 ring-slate-900/5"
    >
      <div class="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/80 pb-3">
        <h5 class="font-semibold text-base text-gray-900">Atividades econômicas secundárias</h5>
        <span
          class="inline-flex shrink-0 items-center rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs font-medium text-slate-700 tabular-nums shadow-sm"
        >
          {{ secondaryActivities.length }}
          {{ secondaryActivities.length === 1 ? 'atividade' : 'atividades' }}
        </span>
      </div>
      <div
        class="max-h-[min(20vh,36rem)] overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200/90 bg-white overscroll-y-contain"
      >
        <Accordion class="rounded-lg border-0 shadow-none">
          <AccordionPanel v-for="(activity, index) in secondaryActivities" :value="index" :key="activity.cnae7"
            class="border-b border-slate-100 last:border-b-0 shadow-none">
            <AccordionHeader>
              <div class="flex items-center gap-4">
                <Tag v-bind="getRiskLevelTagAttrs(activity.level_value)" />
                <div>
                  <div class="font-medium text-sm">{{ activity.cnae_text }}</div>
                  <div class="text-xs text-gray-600 font-mono">{{ formatCnae(activity.cnae7) }}</div>
                </div>
              </div>
            </AccordionHeader>
            <AccordionContent>
              <div class="p-3  rounded-md bg-gray-100">
                <h6 class="font-semibold text-sm mb-2">Hierarquia do CNAE</h6>
                <ul class="space-y-2">
                  <li v-for="(item, index) in getCnaeHierarchy(activity.cnae7)" :key="item.level"
                    class="flex items-baseline">
                    <span class="font-semibold text-gray-600 w-24">{{ item.level }}:</span>
                    <div class="flex-1 indent-4" :style="{ paddingLeft: `${index * 1.5}rem` }">
                      <span class="font-mono text-gray-800">{{ item.code }}</span> -
                      <span class="text-gray-700">{{ item.description }}</span>
                    </div>
                  </li>
                </ul>
              </div>
            </AccordionContent>
          </AccordionPanel>
        </Accordion>
      </div>
    </div>

    <!-- Sem dados -->
    <div v-if="activities.length === 0" class="text-sm text-gray-500 italic">
      Nenhuma atividade econômica encontrada.
    </div>
  </div>
</template>
